import { channels, fromHsl, mix, readableOn, shade, toHsl } from "./colorMath";
import type { AppThemeSpec } from "./themeSpecs";

type Ramp = Record<string, string>;

const accentRamp = (accent: string, mode: "light" | "dark"): Ramp => {
  const { l } = toHsl(accent);
  if (mode === "dark") {
    return {
      50: shade(accent, 0.12, 0.55),
      75: shade(accent, 0.145, 0.5),
      100: shade(accent, 0.175, 0.6),
      200: shade(accent, 0.24, 0.65),
      300: shade(accent, 0.33, 0.7),
      400: shade(accent, Math.max(0.6, l)),
      500: accent,
      base: accent,
      600: shade(accent, 0.7),
      700: shade(accent, 0.79),
      800: shade(accent, 0.88),
      900: shade(accent, 0.94),
    };
  }
  return {
    50: shade(accent, 0.965, 0.55),
    75: shade(accent, 0.94, 0.5),
    100: shade(accent, 0.9, 0.75),
    200: shade(accent, 0.8, 0.85),
    300: shade(accent, 0.68, 0.95),
    400: shade(accent, Math.min(0.62, l + 0.08)),
    500: accent,
    base: accent,
    600: shade(accent, l * 0.86),
    700: shade(accent, l * 0.7),
    800: shade(accent, l * 0.55),
    900: shade(accent, l * 0.42),
  };
};

const neutralRamp = (hue: number, saturation: number, mode: "light" | "dark"): Ramp => {
  const step = (lightness: number, saturationScale = 1) =>
    fromHsl({ h: hue, s: saturation * saturationScale, l: lightness });
  if (mode === "dark") {
    return {
      50: step(0.145),
      100: step(0.19),
      200: step(0.26),
      300: step(0.52, 0.7),
      400: step(0.68, 0.55),
      500: step(0.93, 0.35),
      base: step(0.93, 0.35),
      600: step(0.945, 0.3),
      700: step(0.96, 0.25),
      800: step(0.975, 0.2),
      900: step(1),
    };
  }
  return {
    50: step(0.96, 0.5),
    100: step(0.88, 0.6),
    200: step(0.76, 0.7),
    300: step(0.64, 0.8),
    400: step(0.45, 0.9),
    500: step(0.05),
    base: step(0.05),
    600: step(0.04),
    700: step(0.03),
    800: step(0.02),
    900: step(0.01),
  };
};

const materialRamp = (hue: number, saturation: number, mode: "light" | "dark"): Ramp => {
  const step = (lightness: number, saturationScale = 1) =>
    fromHsl({ h: hue, s: saturation * saturationScale, l: lightness });
  const levels =
    mode === "dark"
      ? [0.13, 0.155, 0.2, 0.26, 0.35, 0.55, 0.7, 0.78, 0.87, 0.93]
      : [0.98, 0.96, 0.93, 0.88, 0.74, 0.62, 0.46, 0.38, 0.26, 0.13];
  const [s50, s100, s200, s300, s400, s500, s600, s700, s800, s900] = levels;
  return {
    50: step(s50, 0.45),
    100: step(s100, 0.5),
    200: step(s200, 0.55),
    300: step(s300, 0.6),
    400: step(s400, 0.7),
    500: step(s500, 0.7),
    600: step(s600, 0.8),
    700: step(s700, 0.85),
    800: step(s800, 0.9),
    900: step(s900),
    A100: step(s100, 0.5),
    A200: step(s200, 0.55),
    A400: step(s400, 0.7),
    A700: step(s700, 0.85),
  };
};

export const buildNeutrals = (spec: AppThemeSpec) => {
  const { mode, base, page, text, neutralHue: hue, neutralSaturation: saturation } = spec;
  const dark = mode === "dark";
  const step = (lightness: number, saturationScale = 1) =>
    fromHsl({ h: hue, s: saturation * saturationScale, l: lightness });

  const sunken = dark ? mix(base, page, 0.55) : mix(base, page, 0.55);
  const muted = dark ? mix(base, page, 0.85) : step(0.925, 0.9);
  const border = dark ? step(0.24, 0.8) : step(0.9, 0.8);
  const borderStrong = dark ? step(0.33, 0.75) : step(0.81, 0.85);

  return {
    page,
    base,
    raised: dark ? mix(base, "#FFFFFF", 0.05) : base,
    sunken,
    muted,
    overlay: dark ? mix(base, "#FFFFFF", 0.04) : base,
    hover: dark ? "rgba(255, 255, 255, 0.06)" : `rgba(${channels(text)}, 0.05)`,
    selected: `rgba(${channels(spec.accent)}, ${dark ? 0.2 : 0.12})`,
    border,
    borderStrong,
    text,
    textSecondary: dark ? step(0.68, 0.5) : step(0.42, 0.75),
    textTertiary: dark ? step(0.52, 0.55) : step(0.6, 0.6),
    textDisabled: dark ? step(0.38, 0.5) : step(0.74, 0.5),
    scrim: dark ? "rgba(0, 0, 0, 0.62)" : `rgba(${channels(text)}, 0.45)`,
    shadow: dark ? "rgba(0, 0, 0, 0.55)" : `rgba(${channels(text)}, 0.12)`,
    brandSpot: dark ? step(0.93, 0.25) : "#FFFFFF",
    onBrandSpot: "#0C0C0C",
    sidebar: dark ? step(0.11, 0.9) : "#233044",
    sidebarMuted: dark ? step(0.17) : step(0.94, 0.5),
    sidebarText: dark ? step(0.93, 0.3) : "#EEEEEE",
    sidebarHover: dark ? step(0.16, 0.9) : "#1E293A",
    sidebarActive: dark ? step(0.22, 0.9) : "#1E253A",
    baseRgb: channels(base),
    textRgb: channels(text),
  };
};

export const buildAccentGroups = (spec: AppThemeSpec) => {
  const { mode, accent, neutralHue: hue, neutralSaturation: saturation } = spec;
  const dark = mode === "dark";
  const neutrals = buildNeutrals(spec);
  const step = (lightness: number, saturationScale = 1) =>
    fromHsl({ h: hue, s: saturation * saturationScale, l: lightness });

  return {
    orange: accentRamp(accent, mode),
    gray: neutralRamp(hue, saturation, mode),
    muiGrey: materialRamp(hue, saturation, mode),
    muiBlueGrey: materialRamp(hue, Math.max(saturation, 0.12) * 1.6, mode),
    warmWhite: {
      base: neutrals.base,
      soft: dark ? mix(neutrals.base, "#FFFFFF", 0.03) : mix(neutrals.base, accent, 0.02),
      strong: dark ? mix(neutrals.base, "#FFFFFF", 0.08) : mix(neutrals.base, accent, 0.06),
    },
    lightGray: {
      base: neutrals.sunken,
      medium: neutrals.muted,
      soft: dark ? mix(neutrals.base, neutrals.page, 0.35) : step(0.975, 0.6),
    },
    inverse: {
      base: dark ? step(0.93, 0.3) : step(0.05),
      hover: dark ? step(1) : step(0.15),
      text: dark ? step(0.06) : readableOn(step(0.05)),
    },
  };
};
